import { Injectable } from '@angular/core';
import { CanActivate, ActivatedRouteSnapshot, RouterStateSnapshot, Router } from '@angular/router';
import { Observable, from, of } from 'rxjs';
import { map, catchError, timeout } from 'rxjs/operators';
import { APIGestaCardService } from '../api/gestacard.service';
import { AlertsService } from 'app/shared/alerts.service';
import { logger } from '../utils/console';

/**
 * WebviewGuard - Valida token da URL antes de permitir acesso à webview
 * 
 * IMPORTANTE: Este guard valida o token SEM salvar no localStorage
 * - O token é lido automaticamente da URL pelo ApiService
 * - NÃO afeta a sessão do navegador (o usuário logado permanece o mesmo)
 * - Permite visualização temporária sem fazer login completo
 * - Ao fechar a aba, o token não persiste
 */
@Injectable({
    providedIn: 'root'
})
export class WebviewGuard implements CanActivate {

  constructor(
    private router: Router,
    private apiGestaCard: APIGestaCardService,
    private alertsService: AlertsService
  ) {}

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean> {

    const key = route.queryParams['key'];

    // 1. Verifica se key existe na URL
    if (!key) {
      logger.warn('WebviewGuard: Nenhuma key fornecida na URL');
      this.alertsService.SnackMessage('Link inválido: token não encontrado');
      this.router.navigate(['/auth/login']);
      return of(false);
    }

    // 2. Valida token na API ANTES de renderizar componente
    // USA validateTokenWithoutLogin para NÃO afetar a sessão do navegador
    return from(this.apiGestaCard.validateTokenWithoutLogin(key)).pipe(
      timeout(10000), // Timeout de 10 segundos
      map((usuario) => {
        // Token válido! Permite acesso
        logger.log('WebviewGuard: Token válido, usuário autenticado:', usuario.email);
        return true;
      }),
      catchError((error) => {
        // Token inválido, expirado ou erro na API
        logger.error('WebviewGuard: Falha na validação do token', error);
        
        let errorMessage = 'Link inválido ou expirado';
        if (error.name === 'TimeoutError') {
          errorMessage = 'Tempo de validação excedido. Tente novamente.';
        } else if (error.status === 401) {
          errorMessage = 'Link expirado. Solicite um novo link.';
        } else if (error.status === 403) {
          errorMessage = 'Você não tem permissão para acessar este conteúdo.';
        }
        
        this.alertsService.SnackMessage(errorMessage);
        this.router.navigate(['/auth/login']);
        return of(false);
      })
    );
  }
}
